/**
 * Pivot point computation for transform operations.
 *
 * The pivot determines the center point around which rotations and scales
 * operate. Blender-style individual origins is modeled separately because it
 * does not resolve to a single transform center.
 *
 *  - median:  Center of mass of the current selection.
 *  - world:   World origin [0,0,0].
 *  - active:  Position of the first item in the selection.
 *  - individual: Per-element origins; callers should use median only as a
 *                display fallback when a single gizmo anchor is needed.
 *  - cursor:  The 3D cursor position (caller must supply it).
 *
 * Works uniformly in both object mode (operating on object positions) and
 * edit mode (operating on vertex positions in object-local space).
 */

import type { PivotReference } from "./types.ts";

export type SinglePivotReference = Exclude<PivotReference, "individual">;

/** Individual Origins does not produce one canonical pivot point. */
export function usesIndividualOrigins(reference: PivotReference): boolean {
  return reference === "individual";
}

/**
 * Compute the pivot point from an ordered array of 3D positions.
 *
 * @param positions       Ordered list of [x,y,z] tuples. In object mode these
 *                        are object world positions; in edit mode they are
 *                        either vertex local positions or (when resolving the
 *                        cursor pivot in edit mode) a pre-converted world-space
 *                        cursor.
 * @param reference       Which pivot strategy to use.
 * @param cursorPosition  Required when `reference === "cursor"`. If omitted
 *                        while cursor is requested, the function falls back to
 *                        `"median"`.
 * @returns               The computed pivot point, or [0,0,0] if positions is empty
 *                        (non-cursor references only — cursor always returns the
 *                        cursor position regardless of selection).
 */
export function computePivot(
  positions: ReadonlyArray<readonly [number, number, number]>,
  reference: PivotReference,
  cursorPosition?: readonly [number, number, number],
): [number, number, number] {
  if (reference === "cursor") {
    if (cursorPosition) {
      return [cursorPosition[0], cursorPosition[1], cursorPosition[2]];
    }
    // Fall back to median if the caller forgot to supply a cursor — never crash.
    return computePivot(positions, "median");
  }

  if (positions.length === 0) return [0, 0, 0];

  if (reference === "individual") {
    return computePivot(positions, "median");
  }

  switch (reference) {
    case "world":
      return [0, 0, 0];

    case "active":
      // "Active" = first item in the selection
      return [...positions[0]] as [number, number, number];

    case "median": {
      let x = 0;
      let y = 0;
      let z = 0;
      for (const p of positions) {
        x += p[0];
        y += p[1];
        z += p[2];
      }
      const n = positions.length;
      return [x / n, y / n, z / n];
    }
  }
}

/** Scale a point from a pivot in either uniform, axis, or arbitrary-direction mode. */
export function scalePointFromPivot(
  point: readonly [number, number, number],
  pivot: readonly [number, number, number],
  factor: number,
  axis?: "x" | "y" | "z" | null,
  direction?: readonly [number, number, number] | null,
): [number, number, number] {
  const ox = point[0] - pivot[0];
  const oy = point[1] - pivot[1];
  const oz = point[2] - pivot[2];

  if (direction) {
    const [dx, dy, dz] = direction;
    const len2 = dx * dx + dy * dy + dz * dz;
    if (len2 <= 1e-12) return [point[0], point[1], point[2]];
    const dot = (ox * dx + oy * dy + oz * dz) / len2;
    return [
      pivot[0] + ox + (factor - 1) * dot * dx,
      pivot[1] + oy + (factor - 1) * dot * dy,
      pivot[2] + oz + (factor - 1) * dot * dz,
    ];
  }

  const fx = axis === "x" ? factor : axis ? 1 : factor;
  const fy = axis === "y" ? factor : axis ? 1 : factor;
  const fz = axis === "z" ? factor : axis ? 1 : factor;
  return [pivot[0] + ox * fx, pivot[1] + oy * fy, pivot[2] + oz * fz];
}

/** Rotate a point around an arbitrary axis and pivot. */
export function rotatePointFromPivot(
  point: readonly [number, number, number],
  pivot: readonly [number, number, number],
  axis: readonly [number, number, number],
  angle: number,
): [number, number, number] {
  const ax = axis[0];
  const ay = axis[1];
  const az = axis[2];
  const axisLen = Math.hypot(ax, ay, az);
  if (axisLen <= 1e-12 || Math.abs(angle) <= 1e-12)
    return [point[0], point[1], point[2]];

  const ux = ax / axisLen;
  const uy = ay / axisLen;
  const uz = az / axisLen;
  const ox = point[0] - pivot[0];
  const oy = point[1] - pivot[1];
  const oz = point[2] - pivot[2];

  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const dot = ox * ux + oy * uy + oz * uz;

  const rx = ox * cos + (uy * oz - uz * oy) * sin + ux * dot * (1 - cos);
  const ry = oy * cos + (uz * ox - ux * oz) * sin + uy * dot * (1 - cos);
  const rz = oz * cos + (ux * oy - uy * ox) * sin + uz * dot * (1 - cos);

  return [pivot[0] + rx, pivot[1] + ry, pivot[2] + rz];
}
